import { useCallback, useEffect, type RefObject } from 'react';

/**
 * The behaviour that makes a thing a modal dialog: Tab stays inside it, Escape
 * closes it, and focus goes back where it came from.
 *
 * **Extracted rather than copied**, which is Article 3's rule — `ConsentDialog`
 * would otherwise have been a second implementation of what `DetailDrawer`
 * already did, and two focus traps eventually disagree about which one handles
 * Escape. The two components still differ in every visual respect; what they
 * share is exactly this, and nothing here knows what either looks like.
 *
 * Not exported from the package barrel: it is a detail of how the two dialogs
 * are built, not something a consumer composes with.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current;
    if (!panel) return [];
    /*
      No `offsetParent` visibility filter: the panel is inside a `position:
      fixed` container, and `offsetParent` is null for fixed descendants in real
      browsers as well as in jsdom — it would discard every candidate. The
      selector already excludes disabled and `tabindex="-1"`.
    */
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (element) => element.getAttribute('aria-hidden') !== 'true',
    );
  }, [panelRef]);

  // Remember the opener, move focus in, and put it back on close.
  useEffect(() => {
    if (!open) return;
    const restoreFocusTo = document.activeElement as HTMLElement | null;
    const first = focusables()[0] ?? panelRef.current;
    first?.focus();
    return () => {
      restoreFocusTo?.focus();
    };
  }, [open, focusables, panelRef]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const elements = focusables();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, focusables]);
}
